/** Per-company MRU of opened desk docs — plan/35 Wave F. */

export type RecentDocKind =
  | "salesOrder"
  | "salesInvoice"
  | "purchaseInvoice"
  | "purchaseOrder"
  | "posTicket"
  | "return"
  | "customerPayment"
  | "supplierPayment"
  | "salesDelivery"
  | "goodsReceipt"
  | "salesShipment"
  | "purchaseShipment"
  | "stockConversion"
  | "journalEntry"
  | "product";

export type RecentDoc = {
  kind: RecentDocKind;
  id: string;
  label: string;
  path: string;
  hint?: string;
  at: number;
};

const PREFIX = "icraftx.recentDocs:";
const MAX = 12;

function key(companySlug: string): string {
  return `${PREFIX}${companySlug}`;
}

export function loadRecentDocs(companySlug: string): RecentDoc[] {
  if (typeof localStorage === "undefined" || !companySlug) return [];
  try {
    const raw = localStorage.getItem(key(companySlug));
    if (!raw) return [];
    const parsed = JSON.parse(raw) as { docs?: RecentDoc[] };
    if (!Array.isArray(parsed?.docs)) return [];
    return parsed.docs.filter(
      (d) =>
        d &&
        typeof d.id === "string" &&
        typeof d.path === "string" &&
        typeof d.label === "string" &&
        typeof d.kind === "string",
    );
  } catch {
    return [];
  }
}

function save(companySlug: string, docs: RecentDoc[]): void {
  try {
    localStorage.setItem(key(companySlug), JSON.stringify({ docs }));
  } catch {
    /* private mode / quota */
  }
}

/** Push or bump a doc to the front of the MRU. */
export function pushRecentDoc(
  companySlug: string,
  input: Omit<RecentDoc, "at"> & { at?: number },
): RecentDoc[] {
  if (!companySlug || !input.id || !input.path) {
    return loadRecentDocs(companySlug);
  }
  const next: RecentDoc = {
    kind: input.kind,
    id: input.id,
    label: input.label,
    path: input.path,
    hint: input.hint,
    at: input.at ?? Date.now(),
  };
  const rest = loadRecentDocs(companySlug).filter(
    (d) => !(d.kind === next.kind && d.id === next.id),
  );
  const out = [next, ...rest].slice(0, MAX);
  save(companySlug, out);
  return out;
}

export function recentDocHint(kind: RecentDocKind): string {
  switch (kind) {
    case "salesOrder":
      return "Sales order";
    case "salesInvoice":
      return "Sales invoice";
    case "purchaseInvoice":
      return "Purchase invoice";
    case "purchaseOrder":
      return "Purchase order";
    case "posTicket":
      return "POS ticket";
    case "return":
      return "Return";
    case "customerPayment":
      return "Customer payment";
    case "supplierPayment":
      return "Supplier payment";
    case "salesDelivery":
      return "Delivery";
    case "goodsReceipt":
      return "Goods receipt";
    case "salesShipment":
      return "In transit";
    case "purchaseShipment":
      return "In transit";
    case "stockConversion":
      return "Conversion";
    case "journalEntry":
      return "Journal";
    case "product":
      return "Product";
    default:
      return "Doc";
  }
}
